import type {
  OrgDepartmentTreeNode,
  OrgDirectoryAdapter,
  OrgMember,
  OrgMemberSearchParams,
  OrgPagedResult,
} from '@zhiyue/core';
import { BizAppConfig } from 'config/application.manifest';
import { createRequest } from '@/services/request';

type ApiEnvelope<T> = {
  success?: boolean;
  data?: T;
};

type MemberPayload = Partial<OrgMember> & {
  uuid?: string;
  photo?: string;
};

type MemberPagePayload = {
  list?: MemberPayload[];
  items?: MemberPayload[];
  records?: MemberPayload[];
  total?: number;
  totalCount?: number;
  pageNo?: number;
  page?: number;
  current?: number;
  pageSize?: number;
  limit?: number;
};

const request = createRequest(BizAppConfig.appOrgDirectoryUrl);
const memberLookupCache = new Map<string, Promise<OrgMember>>();

const unwrap = <T,>(payload: T | ApiEnvelope<T>): T => {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ApiEnvelope<T>).success === false
  ) {
    throw new Error('成员目录接口返回失败');
  }

  if (
    payload &&
    typeof payload === 'object' &&
    'data' in payload &&
    (payload as ApiEnvelope<T>).data !== undefined
  ) {
    return (payload as ApiEnvelope<T>).data as T;
  }

  return payload as T;
};

const normalizeMember = (member: MemberPayload): OrgMember => {
  const id = member.memberUuid || member.id || member.uuid;
  if (!id) {
    throw new Error('成员目录接口返回了缺少 memberUuid 的成员');
  }

  return {
    id,
    userId: member.userId,
    memberUuid: member.memberUuid || id,
    name: member.name,
    nickname: member.nickname,
    username: member.username,
    phone: member.phone,
    email: member.email,
    avatar: member.avatar || member.photo,
  };
};

export const getDepartmentDirectoryTree = async (): Promise<
  OrgDepartmentTreeNode[]
> => {
  const response = await request<unknown[] | ApiEnvelope<unknown[]>>(
    '/bff/iam-ui/departments/tree',
    { method: 'GET' },
  );
  const normalizeNode = (node: any): OrgDepartmentTreeNode => ({
    id: node.id || node.nodeId || node.orgId,
    name: node.name,
    children: node.children?.map(normalizeNode),
  });
  return unwrap(response).map(normalizeNode);
};

const matchesKeyword = (member: OrgMember, keyword?: string) => {
  const normalizedKeyword = keyword?.trim().toLocaleLowerCase();
  if (!normalizedKeyword) return true;

  return [
    member.name,
    member.nickname,
    member.username,
    member.phone,
    member.email,
  ].some(value => value?.toLocaleLowerCase().includes(normalizedKeyword));
};

const normalizePage = (
  payload: MemberPagePayload,
  keyword?: string,
): OrgPagedResult<OrgMember> => {
  const list = payload.list ?? payload.items ?? payload.records ?? [];
  const normalizedList = list.map(normalizeMember);
  const filteredList = normalizedList.filter(member => matchesKeyword(member, keyword));
  const isSearching = Boolean(keyword?.trim());

  return {
    // The directory BFF is expected to apply `keyword`, but some deployments
    // return an unfiltered page. Keep the picker accurate in either case.
    list: filteredList,
    total: isSearching
      ? filteredList.length
      : payload.total ?? payload.totalCount ?? normalizedList.length,
    pageNo: payload.pageNo ?? payload.page ?? payload.current ?? 1,
    pageSize: payload.pageSize ?? payload.limit ?? normalizedList.length,
  };
};

export const getMemberDirectoryMember = async (
  memberUuid: string,
): Promise<OrgMember> => {
  const cached = memberLookupCache.get(memberUuid);
  if (cached) return cached;

  const lookup = (async () => {
    try {
      const response = await request<
        MemberPayload | ApiEnvelope<MemberPayload>
      >(`/bff/iam-ui/members/${encodeURIComponent(memberUuid)}`, {
        method: 'GET',
        skipErrorHandler: true,
      });
      return normalizeMember(unwrap(response));
    } catch {
      // 部分 IAM UI 环境没有单成员接口，回退到分页目录中按稳定标识匹配。
    }

    const pageSize = 100;
    for (let pageNo = 1; pageNo <= 20; pageNo += 1) {
      const response = await request<
        MemberPagePayload | ApiEnvelope<MemberPagePayload>
      >('/bff/iam-ui/members', {
        method: 'GET',
        params: { pageNo, pageSize },
        skipErrorHandler: true,
      });
      const payload = unwrap(response);
      const members = (payload.list ?? payload.items ?? payload.records ?? [])
        .map(normalizeMember);
      const matched = members.find(
        member => member.memberUuid === memberUuid || member.id === memberUuid,
      );
      if (matched) return matched;

      const total = payload.total ?? payload.totalCount;
      if (members.length < pageSize || (total && pageNo * pageSize >= total)) {
        break;
      }
    }

    throw new Error('未在当前企业人员目录中找到该人员');
  })();

  memberLookupCache.set(memberUuid, lookup);
  try {
    return await lookup;
  } catch (error) {
    memberLookupCache.delete(memberUuid);
    throw error;
  }
};

/**
 * 兼容 IAM UI 的直出分页数据与 { success, data } 包装数据，供人员选择器统一使用。
 */
export const memberDirectoryAdapter: OrgDirectoryAdapter = {
  async searchMembers(params: OrgMemberSearchParams) {
    const response = await request<MemberPagePayload | ApiEnvelope<MemberPagePayload>>(
      '/bff/iam-ui/members',
      {
        method: 'GET',
        params: {
          keyword: params.keyword,
          departmentId: params.departmentId,
          includeChildrenNodes: params.includeChildrenNodes,
          pageNo: params.pageNo ?? 1,
          pageSize: params.pageSize ?? 100,
        },
      },
    );
    return normalizePage(unwrap(response), params.keyword);
  },

  async getMemberById(memberUuid: string) {
    return getMemberDirectoryMember(memberUuid);
  },

  async getDepartmentTree() {
    return getDepartmentDirectoryTree();
  },
};
